<template lang="pug">
  div(ref="editor" @drop.prevent="onDrop")
</template>

<script>
import Quill from 'quill';
import MagicUrl from 'quill-magic-url';
import QuillImageDropAndPaste from 'quill-image-drop-and-paste';
import utils from '@/utils/common';

export default {
  name: 'WysiwygEditor',
  props: {
    toolbar: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: '',
    },
    error: Boolean,
  },
  data() {
    return {
      quill: null,
      html: '',
      htmlForChangeEvent: '', // not needed anymore?
      files: [],
    };
  },
  computed: {
    emitFilesDebounced() {
      return utils.debounce(() => {
        this.$emit('dropFiles', this.files);
        this.files = [];
      });
    },
  },
  mounted() {
    Quill.register('modules/magicUrl', MagicUrl);
    Quill.register('modules/imageDropAndPaste', QuillImageDropAndPaste);

    this.quill = new Quill(this.$refs.editor, {
      placeholder: this.label,
      modules: {
        toolbar: this.toolbar,
        magicUrl: true,
        imageDropAndPaste: {
          handler: this.imagePasteHandler,
        },
      },
    });

    this.setHtml(this.value);

    this.quill.on('text-change', () => {
      this.html = this.getHtml();

      this.$emit('input', this.html);
    });

    this.quill.root.addEventListener('blur', () => {
      this.$emit('change', this.getHtml());
    });

    this.quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
      // commented out attributes remain
      const attributesToSkip = {
        background: null,
        // bold: null,
        color: null,
        font: null,
        code: null,
        // italic: null,
        // link: null,
        size: null,
        strike: null,
        script: null,
        // underline: null,
        // blockquote: null,
        header: null,
        indent: null,
        // list: null,
        align: null,
        direction: null,
        // 'code-block': null,
      };

      const Delta = Quill.import('delta');
      const deltaToCompose = new Delta().retain(delta.length(), attributesToSkip);

      return new Delta(
        delta
          .compose(deltaToCompose)
          .filter((op) => typeof op.insert === 'string'),
      );
    });
  },
  methods: {
    imagePasteHandler(imageDataUrl, type, imageData) {
      this.files.push(imageData.toFile());
      this.emitFilesDebounced.deferred();
    },
    onDrop(e) {
      const { files } = e.dataTransfer;

      if (!files || !files.length) return;

      this.$emit('dropFiles', files);
    },
    getHtml() {
      let html = this.$refs.editor.children[0].innerHTML;
      if (html === '<p><br></p>') html = '';

      return html;
    },
    setHtml(newVal) {
      if (newVal !== '' && newVal === this.html) return;

      if (!newVal) {
        this.quill.setText('');
        return;
      }

      this.htmlForChangeEvent = newVal;
      this.quill.pasteHTML(newVal);
    },
  },
  watch: {
    value(newVal) {
      this.setHtml(newVal);
    },
    error(newVal) {
      if (newVal) {
        this.$refs.editor.classList.add('error');
      } else {
        this.$refs.editor.classList.remove('error');
      }
    },
  },
};
</script>

<style lang="scss">
@import "~quill/dist/quill.core";

.ql {
  &-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    position: relative;
    top: 8px;
  }

  &-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 100%;
    transition: background-color 0.1s;
    &:hover {
      background-color: var(--color-super-light);
    }
    &.ql-active {
      background-color: var(--color-super-light);
    }
    &:not(:last-child) {
      margin-right: -4px;
    }
    svg {
      display: block;
      width: 24px;
      height: 24px;
    }
  }

  &-container {
    align-self: normal;
    border-radius: 6px;
    background-color: var(--color-super-light);
    min-height: 130px;
    height: auto;
    word-break: break-word;

    &.error {
      border: 1px solid red;
    }
  }
  &-editor {
    color: var(--color-dark-gray);
    font-weight: 500;
    font-size: 13px;
    padding: 12px 16px;
    &.ql-blank {
      &:before {
        font-family: "Geometria", sans-serif;
        font-style: normal;
        color: var(--color-middle-gray);
        font-weight: 500;
        font-size: 13px;
      }
    }
    p {
      font-family: "Geometria", sans-serif;
    }
    ol,
    ul {
      padding-left: 0;
    }
    li:not(.ql-direction-rtl)::before {
      margin-left: 0;
      margin-right: 7px;
      text-align: left;
      width: 25px;
    }
    strong {
      font-weight: 700;
    }
    pre {
      position: relative;
      display: block;
      padding: 16px 15px 18px 28px;
      border-radius: 6px;
      background-color: var(--color-purple);
      font-family: "Geometria", sans-serif;
      font-style: normal;
      color: var(--color-dark-gray);
      font-weight: 500;
      font-size: 13px;
      left: -1px;
      white-space: pre-wrap
    }
    a {
      border-bottom: 1px solid var(--color-blue);
      color: #2B69BE;
    }
    blockquote {
      display: block;
      border-radius: 6px;
      padding: 16px 15px 16px 28px;
      background-color: var(--color-orange-bg);
      font-family: "Geometria", sans-serif;
      line-height: 15px;
      color: var(--color-dark-gray);
      font-weight: 500;
      font-size: 13px;
      margin-bottom: 10px;
    }
  }
}
.ql-editor ol li:not(.ql-direction-rtl), .ql-editor ul li:not(.ql-direction-rtl) {
  padding-left: 0;
}

.ql-editor {
  font-weight: 300;
  font-size: 14px;
  line-height: 18px;
  color: #1E3147;
  font-family: "Geometria", sans-serif;

  ol {
    list-style: none;
    counter-reset: base-text-counter;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    margin: 6px 0 10px 45px;
    padding: 0;

    li {
      counter-increment: base-text-counter;
      margin-bottom: 0.5em;

      &:before {
        margin: 0 20px 0 -45px !important;
        float: left;
        content: counter(base-text-counter);
        text-align: right !important;
        color: #33A1FB;
        font-weight: bold;
      }
    }
  }

  ul {
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    padding: 0;
    margin: 6px 0 10px 45px;

    li {
      counter-increment: base-text-counter;
      width: 100%;
      margin-bottom: 0.5em;

      &:before {
        margin: 5px 0 0 -45px !important;
        float: left;
        text-align: right !important;
        content: '\2022';
        color: #33A1FB;
        font-weight: bold;
        font-size: 20px;
        line-height: .5;
      }
    }
  }
}
</style>
